import { createContext, useContext, useMemo } from 'react'

import useGetPosts from 'src/hook/useGetPosts'
import { PubDateOrderValue } from 'src/services/posts'
import { PostProps } from 'src/hook/usePosts'

import { PinnedPostsContextProps, PinnedPostsContextProviderProps } from './pinnedPosts.types'

const PinnedPostsContext = createContext<PinnedPostsContextProps | null>(null)

const PinnedPostsContextProvider = ({
  children,
  communityId,
  communityType
}: PinnedPostsContextProviderProps): JSX.Element => {
  const query = useGetPosts({
    enabled: true,
    id: communityId,
    filters: { pinned: true, pubDateOrder: PubDateOrderValue.DESC },
    communityType
  })

  const pinnedPosts = useMemo(() => {
    if (!query.data) {
      return []
    }

    return query.data.pages.reduce((posts, page) => {
      if (!page?.news?.data?.length) {
        return posts
      }

      return [...posts, ...page.news.data]
    }, [] as PostProps[])
  }, [query.data])

  const highLighted = useMemo(() => pinnedPosts.slice(0, 1), [pinnedPosts])

  return (
    <PinnedPostsContext.Provider
      value={{
        pinnedPosts,
        highLighted,
        pinnedPostsQuery: query
      }}
    >
      {children}
    </PinnedPostsContext.Provider>
  )
}

export const usePinnedPostsContext = () => {
  const context = useContext(PinnedPostsContext)

  if (!context) {
    throw new Error('Pinned Posts Context must be used within PinnedPostsContextProvider')
  }

  return context
}

export default PinnedPostsContextProvider
